// FEEL evaluator — tree-walk interpreter over the parser's AST. The result
// type is plain JS: number / string / boolean / null / Array / Object.
// Errors are thrown; callers can wrap for policy (e.g. null propagation).

import { parse } from './Parser';
import { resolveBuiltin, truthy, deepEqual } from './builtins';

export class FeelEvalError extends Error {
  constructor(msg) { super(`[feel/eval] ${msg}`); }
}

class Env {
  constructor(parent = null, bindings = {}) {
    this.parent = parent;
    this.bindings = { ...bindings };
  }
  lookup(name) {
    if (Object.prototype.hasOwnProperty.call(this.bindings, name)) return this.bindings[name];
    if (this.parent) return this.parent.lookup(name);
    const fn = resolveBuiltin(name);
    return fn != null ? fn : undefined;
  }
  extend(bindings) { return new Env(this, bindings); }
}

export function evaluate(source, context = {}) {
  const ast = typeof source === 'string' ? parse(source) : source;
  return evalNode(ast, new Env(null, context));
}

export function evalAst(ast, context = {}) {
  return evalNode(ast, new Env(null, context));
}

function evalNode(node, env) {
  if (!node) return null;
  switch (node.type) {
    case 'Literal':   return node.value;
    case 'Ident':     return evalIdent(node, env);
    case 'List':      return node.items.map((it) => evalNode(it, env));
    case 'Context':   return evalContext(node, env);
    case 'Unary':     return evalUnary(node, env);
    case 'Binary':    return evalBinary(node, env);
    case 'Logical':   return evalLogical(node, env);
    case 'Compare':   return evalCompare(node, env);
    case 'Between':   return evalBetween(node, env);
    case 'In':        return evalIn(node, env);
    case 'If':        return truthy(evalNode(node.cond, env))
                        ? evalNode(node.then, env)
                        : evalNode(node.else, env);
    case 'For':       return evalFor(node, env);
    case 'Quantified':return evalQuantified(node, env);
    case 'Path':      return evalPath(node, env);
    case 'Index':     return evalIndex(node, env);
    case 'Call':      return evalCall(node, env);
    default:
      throw new FeelEvalError(`unknown node type '${node.type}'`);
  }
}

function evalIdent(node, env) {
  const v = env.lookup(node.name);
  if (v === undefined) throw new FeelEvalError(`unknown identifier '${node.name}'`);
  return v;
}

function evalContext(node, env) {
  // Entries evaluate with earlier siblings visible (like FEEL boxed contexts).
  const out = {};
  const inner = env.extend({});
  node.entries.forEach(({ key, value }) => {
    const v = evalNode(value, inner);
    out[key] = v;
    inner.bindings[key] = v;
  });
  return out;
}

function evalUnary(node, env) {
  const v = evalNode(node.arg, env);
  if (node.op === '-') {
    if (typeof v !== 'number') throw new FeelEvalError(`unary - expects number, got ${typeof v}`);
    return -v;
  }
  if (node.op === 'not') return !truthy(v);
  throw new FeelEvalError(`unsupported unary '${node.op}'`);
}

function evalBinary(node, env) {
  const l = evalNode(node.left, env);
  const r = evalNode(node.right, env);
  switch (node.op) {
    case '+':
      if (typeof l === 'string' || typeof r === 'string') return String(l) + String(r);
      return numOp(l, r, (a, b) => a + b, '+');
    case '-':  return numOp(l, r, (a, b) => a - b, '-');
    case '*':  return numOp(l, r, (a, b) => a * b, '*');
    case '/':  return numOp(l, r, (a, b) => a / b, '/');
    case '**': return numOp(l, r, (a, b) => a ** b, '**');
  }
  throw new FeelEvalError(`unsupported binary op '${node.op}'`);
}

function numOp(l, r, fn, op) {
  if (typeof l !== 'number' || typeof r !== 'number')
    throw new FeelEvalError(`operator '${op}' expects numbers`);
  return fn(l, r);
}

function evalLogical(node, env) {
  const l = evalNode(node.left, env);
  if (node.op === 'and') return truthy(l) ? truthy(evalNode(node.right, env)) : false;
  if (node.op === 'or')  return truthy(l) ? true : truthy(evalNode(node.right, env));
  throw new FeelEvalError(`unsupported logical op '${node.op}'`);
}

function evalCompare(node, env) {
  const l = evalNode(node.left, env);
  const r = evalNode(node.right, env);
  switch (node.op) {
    case '=':  return deepEqual(l, r);
    case '!=': return !deepEqual(l, r);
    case '<':  return cmp(l, r) < 0;
    case '<=': return cmp(l, r) <= 0;
    case '>':  return cmp(l, r) > 0;
    case '>=': return cmp(l, r) >= 0;
  }
  throw new FeelEvalError(`unsupported compare op '${node.op}'`);
}

function cmp(a, b) {
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  if (typeof a === 'string' && typeof b === 'string') return a < b ? -1 : a > b ? 1 : 0;
  throw new FeelEvalError(`cannot compare ${typeof a} and ${typeof b}`);
}

function evalBetween(node, env) {
  const v = evalNode(node.value, env);
  const low = evalNode(node.low, env);
  const high = evalNode(node.high, env);
  return cmp(v, low) >= 0 && cmp(v, high) <= 0;
}

function evalIn(node, env) {
  const v = evalNode(node.value, env);
  return node.tests.some((t) => {
    const tv = evalNode(t, env);
    if (Array.isArray(tv)) return tv.some((x) => deepEqual(v, x));
    return deepEqual(v, tv);
  });
}

function evalFor(node, env) {
  const iter = evalNode(node.iter, env);
  const list = Array.isArray(iter) ? iter : [iter];
  return list.map((v) => evalNode(node.body, env.extend({ [node.name]: v })));
}

function evalQuantified(node, env) {
  const iter = evalNode(node.iter, env);
  const list = Array.isArray(iter) ? iter : [iter];
  if (node.quantifier === 'some')
    return list.some((v) => truthy(evalNode(node.body, env.extend({ [node.name]: v }))));
  return list.every((v) => truthy(evalNode(node.body, env.extend({ [node.name]: v }))));
}

function evalPath(node, env) {
  const obj = evalNode(node.obj, env);
  if (obj == null) return null;
  if (typeof obj !== 'object') throw new FeelEvalError(`path on non-object`);
  return obj[node.name];
}

function evalIndex(node, env) {
  const obj = evalNode(node.obj, env);
  const idx = evalNode(node.index, env);
  if (Array.isArray(obj)) {
    if (typeof idx !== 'number') throw new FeelEvalError(`list index must be a number`);
    // FEEL uses 1-based indexing; negative counts from end.
    const i = idx > 0 ? idx - 1 : obj.length + idx;
    return obj[i];
  }
  if (obj && typeof obj === 'object') return obj[idx];
  throw new FeelEvalError(`cannot index non-list/non-context value`);
}

function evalCall(node, env) {
  const fn = evalNode(node.callee, env);
  if (typeof fn !== 'function') throw new FeelEvalError(`attempt to call non-function`);
  const args = node.args.map((a) => evalNode(a, env));
  if (node.isNamed && Object.keys(node.named || {}).length) {
    const named = {};
    Object.entries(node.named).forEach(([k, v]) => { named[k] = evalNode(v, env); });
    args.push(named);
  }
  return fn(...args);
}
